import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Spinner,
  VStack,
} from "@chakra-ui/react";
import { ReactElement, useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { LuPrinter } from "react-icons/lu";
import { GenericTable, TableColumn } from "../ui/table";
import useFetchManagementReceipts from "./hooks/useFetchManagementReceipts";
import useShowToast from "../../hooks/useShowToast";
import api from "../../api";
import { formatCurrency } from "../../utils/numbers";

const ClubManagementReceipts = (): ReactElement => {
  const { t } = useTranslation();
  const [dateFrom, setDateFrom] = useState<Date>(startOfMonth(new Date()));
  const [dateTo, setDateTo] = useState<Date>(endOfMonth(new Date()));
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const { receipts, loading, refetchReceipts } = useFetchManagementReceipts();
  const { errorToast } = useShowToast();

  useEffect(() => {
    refetchReceipts(dateFrom, dateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetFilters = () => {
    const now = new Date();
    setDateFrom(startOfMonth(now));
    setDateTo(endOfMonth(now));
    refetchReceipts(dateFrom, dateTo);
  };

  const refetch = () => {
    refetchReceipts(dateFrom, dateTo);
  };

  const handlePrint = async () => {
    setIsPrintLoading(true);
    await api.printReceiptList(dateFrom, dateTo);
    if (api.error) {
      errorToast({
        title: t("club_management.receipts.print.error_title"),
        description: t("club_management.receipts.print.error_description"),
      });
    }
    setIsPrintLoading(false);
  };

  const columns: TableColumn[] = useMemo(
    () => [
      {
        label: t("club_management.receipts.table.number"),
        key: "number",
        sortable: true,
      },
      {
        label: t("club_management.receipts.table.date"),
        key: "date",
        sortable: true,
        render: (value: unknown) => format(value as Date, "dd/MM/yyyy"),
      },
      {
        label: t("club_management.receipts.table.name"),
        key: "name",
        sortable: true,
      },
      {
        label: t("club_management.receipts.table.amount"),
        key: "amount",
        sortable: true,
        render: (value: unknown) => formatCurrency(value as number),
      },
      {
        label: t("club_management.receipts.table.actions"),
        key: "actions",
        sortable: false,
        render: (_value: unknown, row: Record<string, unknown>) => (
          <Button
            leftIcon={<LuPrinter />}
            size="xs"
            colorScheme="blue"
            onClick={() => window.open(`/receipt/${row.id}`, "_blank")}
          >
            {t("club_management.receipts.table.print")}
          </Button>
        ),
      },
    ],
    [t],
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="200px">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      <Box
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ base: "stretch", md: "center" }}
        gap={3}
        width="100%"
      >
        <Heading size="lg">{t("club_management.receipts.title")}</Heading>
        <Button
          leftIcon={<LuPrinter />}
          colorScheme="blue"
          size="sm"
          onClick={handlePrint}
          isLoading={isPrintLoading}
        >
          {t("club_management.receipts.actions.print")}
        </Button>
      </Box>

      {/* Date Filters */}
      <Box p={4} bg="gray.50" borderRadius="md" className="no-print">
        <Box
          display="flex"
          flexDirection={{ base: "column", md: "row" }}
          gap={4}
          alignItems={{ base: "stretch", md: "end" }}
        >
          <FormControl>
            <FormLabel>{t("club_management.receipts.filters.date_from")}</FormLabel>
            <Input
              type="date"
              value={format(dateFrom, "yyyy-MM-dd")}
              onChange={e => setDateFrom(new Date(e.target.value))}
              size="sm"
            />
          </FormControl>
          <FormControl>
            <FormLabel>{t("club_management.receipts.filters.date_to")}</FormLabel>
            <Input
              type="date"
              value={format(dateTo, "yyyy-MM-dd")}
              onChange={e => setDateTo(new Date(e.target.value))}
              size="sm"
            />
          </FormControl>
          <Button onClick={refetch} size="sm" pl={8} pr={8} colorScheme="green">
            {t("club_management.receipts.filters.filter")}
          </Button>
          <Button onClick={resetFilters} size="sm" pl={8} pr={8} colorScheme="blue">
            {t("club_management.receipts.filters.reset")}
          </Button>
        </Box>
      </Box>

      <GenericTable
        data={receipts as unknown as Record<string, unknown>[]}
        columns={columns}
        size="sm"
      />
    </VStack>
  );
};

export default ClubManagementReceipts;
